import React, { useState, useRef, useEffect } from 'react';
import { useForm } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import MainLayout from '@/pages/layouts/MainLayout';

const ConversationShow = ({ conversation, facility, current_user }) => {
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom when component mounts or messages change
  useEffect(() => {
    scrollToBottom();
  }, [conversation.messages]);
  const { data, setData, post, processing, errors, reset } = useForm({
    content: '',
    conversation_id: conversation.id
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    post(`/conversations/${conversation.id}/messages`, {
      preserveScroll: true,
      onSuccess: () => {
        // Reset the entire form to clear all fields
        reset();
        // Explicitly set content to empty string
        setData('content', '');
      }
    });
  };

  return (
    <div className="container mx-auto py-6 max-w-5xl">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Facility Info */}
        <div className="md:col-span-1">
          <Card className="overflow-hidden shadow-md rounded-xl pt-0">
            {/* Facility Image */}
            <div className="relative">
              <div className="w-full h-[210px] overflow-hidden">
                <img
                  className="w-full h-full object-cover"
                  src={facility.image_url || "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"}
                  alt={facility.name}
                />
              </div>

              {/* Title overlay with gradient background */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <h2 className="text-xl font-semibold text-white">{facility.name}</h2>
                <p className="text-sm text-white/80">{facility.address}</p>
              </div>
            </div>

            <CardContent className="pt-6 space-y-4">
              {/* Care Type */}
              <div>
                <h3 className="text-base font-medium text-gray-800 mb-2">Care Type</h3>
                <div className="flex items-center space-x-2">
                  <div className="bg-blue-100 text-blue-800 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {facility.care_type === 'assisted_living' ? 'AL' :
                     facility.care_type === 'memory_care' ? 'MC' : 'B'}
                  </div>
                  <span className="text-sm">
                    {facility.care_type === 'assisted_living' ? 'Assisted Living' :
                     facility.care_type === 'memory_care' ? 'Memory Care' : 'Both'}
                  </span>
                </div>
              </div>

              {/* Available Units */}
              <div>
                <h3 className="text-base font-medium text-gray-800 mb-2">Available Units</h3>
                {facility.units && facility.units.length > 0 ? (
                  <div className="space-y-2">
                    {facility.units
                      .filter(unit => unit.available)
                      .map(unit => (
                        <div key={unit.id} className="flex justify-between items-center">
                          <p className="text-sm text-gray-600">
                            {unit.room_type === 'studio' ? 'Studio' :
                             unit.room_type === 'one_bedroom' ? '1 Bedroom' :
                             unit.room_type === 'two_bedroom' ? '2 Bedroom' :
                             unit.room_type === 'shared' ? 'Shared Room' :
                             unit.room_type === 'suite' ? 'Suite' : unit.room_type}
                            {unit.bathrooms && ` - ${unit.bathrooms} Bath`}
                          </p>
                          <p className="text-sm font-medium">${unit.price?.toLocaleString()}/mo</p>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No units currently available</p>
                )}
              </div>

              {/* Facility Description */}
              {facility.description && (
                <div>
                  <h3 className="text-base font-medium text-gray-800 mb-1">About</h3>
                  <p className="text-sm text-gray-600">{facility.description}</p>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => window.history.back()}
                className="flex-1"
              >
                Back to My Messages
              </Button>
              <Button
                asChild
                className="flex-1"
              >
                <a href={`/facilities/${facility.slug}`} target="_blank" rel="noopener noreferrer">
                  View Details
                </a>
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Conversation */}
        <div className="md:col-span-2">
          <Card className="shadow-md rounded-xl overflow-hidden flex flex-col h-[700px] pt-0">
            <CardHeader className="border-b bg-gray-50 pt-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Conversation with {facility.name}</CardTitle>
                  <CardDescription>
                    {conversation.status === 'active' ? 'Active conversation' : 'Archived conversation'}
                  </CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-block w-3 h-3 rounded-full ${conversation.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                  <span className="text-sm font-medium">{conversation.status === 'active' ? 'Active' : 'Archived'}</span>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-grow overflow-hidden p-0">
              <div className="space-y-4 h-full overflow-y-auto p-6 bg-gray-50/50">
                {conversation.messages && conversation.messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex ${message.from_user ? 'justify-end' : message.system_message ? 'justify-center' : 'justify-start'} mb-4`}
                  >
                    {!message.from_user && !message.system_message && (
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                        <span className="text-xs font-bold text-blue-800">F</span>
                      </div>
                    )}

                    <div
                      className={`max-w-[75%] rounded-2xl p-4 shadow-sm ${
                        message.system_message
                          ? 'bg-muted text-muted-foreground text-sm italic w-3/4 text-center py-2 px-4 rounded-full shadow-none'
                          : message.from_user
                            ? 'bg-primary text-primary-foreground rounded-tr-none'
                            : 'bg-white border rounded-tl-none'
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{message.content}</div>
                      <div className="text-xs mt-2 opacity-70 flex justify-between items-center">
                        <span className="font-medium">
                          {message.system_message
                            ? 'System'
                            : message.from_user
                              ? 'You'
                              : facility.name}
                        </span>
                        <span>{new Date(message.created_at).toLocaleString()}</span>
                      </div>
                    </div>

                    {message.from_user && (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center ml-2 flex-shrink-0 mt-1">
                        <span className="text-xs font-bold text-primary">Y</span>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} className="h-4" />
              </div>
            </CardContent>

            <CardFooter className="p-4 border-t bg-white">
              <form onSubmit={handleSubmit} className="w-full">
                <div className="flex items-end space-x-2">
                  <Textarea
                    placeholder="Type your message here..."
                    value={data.content}
                    onChange={e => setData('content', e.target.value)}
                    className="flex-grow rounded-xl border-gray-200 focus-visible:ring-primary"
                    rows={2}
                  />
                  <Button
                    type="submit"
                    disabled={processing || !data.content.trim()}
                    className="rounded-xl h-10 px-4"
                  >
                    {processing ? 'Sending...' : 'Send'}
                  </Button>
                </div>
                {errors.content && (
                  <p className="text-sm text-destructive mt-2">{errors.content}</p>
                )}
              </form>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

ConversationShow.layout = page => <MainLayout children={page} title="Conversation" />;

export default ConversationShow;
